import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { Grid, Link } from '@mui/material';
import Fade from '@mui/material/Fade';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useState } from 'react';
import { startOrStopHarvestBots } from 'wisestep-api';
import {
  ScrollableTabs,
  TabTextVariant,
  Button,
  WsTypography,
  WsColors,
  WsPagination,
} from 'wisestep-base';

import useGetCandidateHarvestBots from '../utils/useGetCandidateHarvestBots';
import useHarvestingBotsCount from '../utils/useHarvestingBotsCount';

import { filterList, jobBoardfilterList } from './BotsHelper';

const HarvestBotsList = ({ handleOpenBotDownloadMatchDrawer }) => {
  const botsCount = useHarvestingBotsCount();
  const harvestBotTabsList = [
    {
      tabName: `All Bots (${botsCount?.allBotsCount ?? 0})`,
      allBots: false,
    },
    {
      tabName: `My Bots (${botsCount?.myBotsCount ?? 0})`,
      allBots: true,
    },
  ];
  const [allBots, setAllBots] = useState<boolean>(harvestBotTabsList[0].allBots);
  const [botButtonFilterTitle, setBotButtonFilterTitle] = useState<string>(filterList[1].label);
  const [jobBoardButtonFilterTitle, setJobBoardButtonFilterTitle] = useState<string>(
    jobBoardfilterList[0].label
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorJobBoardEl, setAnchorJobBoardEl] = useState<null | HTMLElement>(null);

  const [status, setStatus] = useState<string>(filterList[1].value);
  const [jobBoard, setJobBoard] = useState<string>(jobBoardfilterList[0].value);
  const open = Boolean(anchorEl);
  const openJobBoard = Boolean(anchorJobBoardEl);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [harvestBots, totalCount] = useGetCandidateHarvestBots(
    jobBoard,
    status,
    pageNumber > 0 ? pageNumber - 1 : 0,
    allBots
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleJobBoardClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorJobBoardEl(event.currentTarget);

  const handleJobBoardClose = () => setAnchorJobBoardEl(null);

  const handleJobBoardMenuClick = (e, jobFilter) => {
    setAnchorJobBoardEl(null);
    setJobBoardButtonFilterTitle(jobFilter.label);
    setJobBoard(jobFilter.value);
  };

  const handleClose = () => setAnchorEl(null);

  const handleBotMenuClick = (e, botFilter) => {
    if (pageNumber < 0) setPageNumber(1);
    setAnchorEl(null);
    setBotButtonFilterTitle(botFilter.label);
    setStatus(botFilter.value);
  };

  const handlePagination = (event, value) => setPageNumber(value + 1);

  const startOrStopEvent = async (harvestBot, isActive: boolean) => {
    const harvestBotList = [...harvestBots];
    const selectedHarvestBotIndex = harvestBotList.findIndex((hb) => hb.id === harvestBot.id);
    harvestBotList.splice(selectedHarvestBotIndex, 1, {
      ...harvestBot,
      isActive,
    });
    harvestBot.isActive = isActive;
    await startOrStopHarvestBots(harvestBot);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 3.6,
      renderCell: (params) => <WsTypography bold>{params.row.name}</WsTypography>,
    },
    {
      field: 'jobBoardName',
      headerName: 'Job Board',
      flex: 1.1,
      renderCell: (params) => <WsTypography bold>{params.row.jobBoardName}</WsTypography>,
    },
    { field: 'lastRunAt', headerName: 'Last Harvested', flex: 1 },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1.3,
      renderCell: (params) => <WsTypography bold>{params.row.createdBy}</WsTypography>,
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
      renderCell: (params) => <WsTypography bold>{params.row.createdOn}</WsTypography>,
    },
    {
      field: 'harvestingInterval',
      headerName: 'Interval',
      flex: 0.6,
    },
    {
      field: 'matchedCandidates',
      headerName: 'Matched',
      flex: 0.6,
      renderCell: (params) => (
        <Grid container sx={{ display: 'flex' }}>
          <Link
            onClick={() => handleOpenBotDownloadMatchDrawer('all', params.row.id)}
            style={{ cursor: 'pointer' }}
          >
            {params.row.matchedCandidates}
          </Link>
        </Grid>
      ),
    },
    {
      field: 'downloadedCandidates',
      headerName: 'Download',
      flex: 0.8,
      renderCell: (params) => (
        <Grid container sx={{ display: 'flex' }}>
          <Link
            onClick={() => handleOpenBotDownloadMatchDrawer('downloaded', params.row.id)}
            style={{ cursor: 'pointer' }}
          >
            {params.row.downloadedCandidates}
          </Link>
        </Grid>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => (
        <Grid container sx={{ display: 'flex' }}>
          <Grid item sx={{ m: 1 }}>
            <Button
              disabled
              id="save-button"
              style={{
                background: params.row.isActive ? WsColors.Green200 : WsColors.GreyBlack,
              }}
              variant="contained"
              size="small"
            >
              {params.row.isActive ? 'Active' : 'Inactive'}
            </Button>
          </Grid>
        </Grid>
      ),
    },
    {
      field: 'play',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <Grid container sx={{ display: 'flex' }}>
          <Grid item sx={{ m: 1 }}>
            {!params.row.isActive ? (
              <Button
                id="play-button"
                variant="outlined"
                size="small"
                disableElevation
                startIcon={<PlayCircleOutlineIcon />}
                onClick={() => startOrStopEvent(params.row, true)}
              >
                Play
              </Button>
            ) : (
              <Button
                id="stop-button"
                variant="outlined"
                size="small"
                disableElevation
                startIcon={<StopCircleIcon />}
                onClick={() => startOrStopEvent(params.row, false)}
              >
                Stop
              </Button>
            )}
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <>
      <ScrollableTabs
        scrollList={harvestBotTabsList}
        getTabIndex={(index: number) => {
          setAllBots(harvestBotTabsList[index].allBots);
          setPageNumber(0);
        }}
        variant={TabTextVariant.SUB_TITLE}
      />
      <Grid item container spacing={2} pb={1} pt={1.5} justifyContent="flex-end">
        <Grid item>
          <Button
            id="fade-button"
            variant="outlined"
            size="large"
            aria-controls={open ? 'fade-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleClick}
          >
            {botButtonFilterTitle}
          </Button>
          <Menu
            id="fade-menu"
            MenuListProps={{
              'aria-labelledby': 'fade-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            TransitionComponent={Fade}
          >
            {filterList &&
              filterList.map((filter, index) => (
                <MenuItem key={index} onClick={(e) => handleBotMenuClick(e, filter)}>
                  {filter.label}
                </MenuItem>
              ))}
          </Menu>
        </Grid>
        <Grid item pr={3}>
          <Button
            id="fade-button-jobboard"
            variant="outlined"
            size="large"
            aria-controls={openJobBoard ? 'fade-menu-jobboard' : undefined}
            aria-haspopup="true"
            aria-expanded={openJobBoard ? 'true' : undefined}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleJobBoardClick}
          >
            {jobBoardButtonFilterTitle}
          </Button>
          <Menu
            id="fade-menu-jobboard"
            MenuListProps={{
              'aria-labelledby': 'fade-button-jobboard',
            }}
            anchorEl={anchorJobBoardEl}
            open={openJobBoard}
            onClose={handleJobBoardClose}
            TransitionComponent={Fade}
          >
            {jobBoardfilterList &&
              jobBoardfilterList.map((filter, index) => (
                <MenuItem key={index} onClick={(e) => handleJobBoardMenuClick(e, filter)}>
                  {filter.label}
                </MenuItem>
              ))}
          </Menu>
        </Grid>
      </Grid>
      <Grid item md={12} style={{ height: 600, width: '100%' }} p={1}>
        <DataGrid
          rows={harvestBots}
          columns={columns}
          pagination={true}
          pageSize={50}
          hideFooterPagination={true}
          rowsPerPageOptions={[50]}
          rowCount={totalCount}
          initialState={{
            sorting: {
              sortModel: [{ field: 'createdOn', sort: 'desc' }],
            },
          }}
        />
      </Grid>
      <WsPagination
        count={Math.ceil(totalCount / 50)}
        onChange={handlePagination}
        itemsPerPage={50}
        pageNumber={pageNumber}
        defaultPage={pageNumber}
        isPageCount={false}
        type="Jobs"
        totalCount={totalCount}
      />
    </>
  );
};
export default HarvestBotsList;
